import { Splash, Tab, TabBar, Tabs, TabView, Line, Heading } from '@salecycle/ui-component-library';
import Spinner from 'core/ui/components/spinner';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useSite } from 'sections/hooks/useSite';
import { TemplateManagementRoute } from '../../routes';
import { LayoutWithStaticSidePanelRight } from './components/styles';
import { TemplateListSidePanel } from './components/TemplateListSidePanel';
import { useCloseSidebarOnNavigationChange } from './hooks/useCloseSidebarOnNavigationChange';
import { SubjectLines } from './components/SubjectLines';
import TemplatesGrid from './components/TemplatesGrid';
import TemplateSorting from './components/TemplateSorting';
import { TemplateTypes } from './constants/templateTypes';
import { tabs, TAB_TO_TEMPLATE_TYPE, MOBILE_CHANNEL_TAB_IDS, getDefaultTabForChannel } from './constants/tabs';
import { useTemplatesListDispatch, useTemplatesListState } from './context/UseTemplatesListContext';
import queryString from 'query-string';
import { useAccessLevel, useFeatureFlags } from 'core/app/AppContext';
import { SelectedTemplateSidebar } from './components/SelectedTemplateSidebar/SelectedTemplateSidebar';
import { updateURLQueryParam } from 'utils/url';

export const TemplatesList: React.FC = () => {
  const history = useHistory();
  const { t } = useTranslation('designs');
  const { accessLevel } = useAccessLevel();
  const dispatch = useTemplatesListDispatch();
  const { siteId, channel } = useParams<{ siteId: string; channel: string }>();
  const { data: site, isError, isLoading } = useSite(siteId);
  const { selectedTemplate } = useTemplatesListState();
  const { flagEnabled } = useFeatureFlags();
  const emailTrackingFeatureEnabled = flagEnabled('ld-email-tracking-consent');
  const globalSiteId = import.meta.env.VITE_GLOBAL_SITE_ID;

  const queryParams = queryString.parse(window.location.search);
  const tabFromQuery = queryParams.templateType?.toString() || queryParams.tab?.toString();

  const [selectedTabId, setSelectedTabId] = React.useState(() => {
    return tabFromQuery || getDefaultTabForChannel(channel);
  });

  // Helper to check if a tab is valid for a channel
  const isValidTabForChannel = React.useCallback(
    (tabId: string, channelParam: string | undefined): boolean => {
      const mpsTabs: string[] = [tabs.MPS_EMBED, tabs.MPS_POPUP];
      const onsiteTabs: string[] = [tabs.DATA_ACQUISITION, tabs.SEND_MY_BASKET, tabs.DISPLAY_ONLY];
      const emailTabs: string[] = [
        tabs.CART_ABANDONMENTS,
        tabs.BROWSE_ABANDONMENTS,
        tabs.SEND_MY_BASKET,
        tabs.SUBJECT_LINES,
        tabs.UNSUBSCRIBES,
        tabs.DATA_ACQUISITION,
        tabs.BULK_RETARGETING,
      ];

      switch (channelParam) {
        case 'mps':
          return mpsTabs.includes(tabId);
        case 'onsite':
          return onsiteTabs.includes(tabId);
        case 'sms':
          return MOBILE_CHANNEL_TAB_IDS.includes(tabId as typeof MOBILE_CHANNEL_TAB_IDS[number]);
        case 'rcs':
          return (
            flagEnabled('ld-rcs') && MOBILE_CHANNEL_TAB_IDS.includes(tabId as typeof MOBILE_CHANNEL_TAB_IDS[number])
          );
        case 'email':
        default:
          return emailTabs.includes(tabId);
      }
    },
    [flagEnabled],
  );

  const prevChannelRef = React.useRef(channel);

  React.useEffect(() => {
    if (!channel || (channel === 'rcs' && !flagEnabled('ld-rcs'))) {
      history.push(generatePath(TemplateManagementRoute.HOME, { siteId, channel: 'email' }));
      return;
    }

    if (queryParams.fromOnsiteSMB === 'true') {
      setSelectedTabId(tabs.SUBJECT_LINES);
      updateURLQueryParam('templateType', tabs.SUBJECT_LINES);
      return;
    }

    const channelChanged = prevChannelRef.current !== channel;
    const defaultTab = getDefaultTabForChannel(channel);

    if (channelChanged) {
      if (tabFromQuery && !isValidTabForChannel(tabFromQuery, channel)) {
        setSelectedTabId(defaultTab);
        updateURLQueryParam('templateType', defaultTab);
      } else if (!tabFromQuery) {
        setSelectedTabId(defaultTab);
        updateURLQueryParam('templateType', defaultTab);
      } else {
        setSelectedTabId(tabFromQuery);
      }
    } else {
      if (tabFromQuery && isValidTabForChannel(tabFromQuery, channel)) {
        setSelectedTabId(tabFromQuery);
      } else if (!tabFromQuery) {
        setSelectedTabId(defaultTab);
        updateURLQueryParam('templateType', defaultTab);
      } else {
        setSelectedTabId(defaultTab);
        updateURLQueryParam('templateType', defaultTab);
      }
    }

    prevChannelRef.current = channel;
  }, [channel, tabFromQuery, siteId, history, isValidTabForChannel, queryParams.fromOnsiteSMB, flagEnabled]);

  // Initial site load
  React.useEffect(() => {
    if (!isLoading && site) {
      dispatch({
        type: 'SET_SUPPORTED_LANGUAGES',
        payload: { supportedLanguages: site.supportedLanguagesSettings?.supportedLanguages ?? [] },
      });
    }
  }, [isLoading, dispatch, site]);

  useCloseSidebarOnNavigationChange(`${selectedTabId}-${channel}`);

  const getTabContent = React.useCallback(() => {
    switch (selectedTabId) {
      case tabs.SUBJECT_LINES:
        return <SubjectLines />;
      case tabs.UNSUBSCRIBES:
        return (
          <>
            {siteId !== globalSiteId && <TemplatesGrid templateType={TemplateTypes.unsubscribe} channel={channel} />}
            {siteId !== globalSiteId && emailTrackingFeatureEnabled && (
              <>
                <Line />
                <Heading variation="h3">{t('designs:designGallery.siteEmailTrackingUnsubscribeHeader')}</Heading>
                <TemplatesGrid templateType={TemplateTypes.unsubscribeEmailTracking} channel={channel} />
              </>
            )}
            {accessLevel === 'SaleCycle' && (
              <>
                <Line />
                <Heading variation="h3">{t('designs:designGallery.globalUnsubscribeHeader')}</Heading>
                <TemplatesGrid templateType={TemplateTypes.globalUnsubscribe} channel={channel} />
                {emailTrackingFeatureEnabled && (
                  <>
                    <Line />
                    <Heading variation="h3">{t('designs:designGallery.globalEmailTrackingUnsubscribeHeader')}</Heading>
                    <TemplatesGrid templateType={TemplateTypes.globalUnsubscribeEmailTracking} channel={channel} />
                  </>
                )}
              </>
            )}
          </>
        );
      default:
        const templateType = TAB_TO_TEMPLATE_TYPE[selectedTabId];
        if (!templateType) {
          throw new Error(`Cannot find a tab with that template type: ${selectedTabId}`);
        }
        return <TemplatesGrid key={`${templateType}-${selectedTabId}`} templateType={templateType} channel={channel} />;
    }
  }, [selectedTabId, channel, siteId, globalSiteId, accessLevel, emailTrackingFeatureEnabled, t]);

  const renderTabs = React.useCallback(() => {
    const mobileTabConfig = [
      { id: tabs.CART_ABANDONMENTS, label: t('designs:designGallery.cartAbandonments') },
      { id: tabs.BROWSE_ABANDONMENTS, label: t('designs:designGallery.browseAbandonments') },
    ];
    const tabConfigs = {
      onsite: [
        {
          id: tabs.DATA_ACQUISITION,
          label: t('designs:designGallery.form.templateType.dataAcquisition'),
          ariaLabel: t('designs:designGallery.tabs.dataAcquisitionAria'),
        },
        {
          id: tabs.SEND_MY_BASKET,
          label: t('designs:designGallery.form.templateType.sendMyBasket'),
          ariaLabel: t('designs:designGallery.tabs.sendMyBasketAria'),
        },
        ...(flagEnabled('ld-onsite-display-only')
          ? [
              {
                id: tabs.DISPLAY_ONLY,
                label: t('designs:designGallery.form.templateType.displayOnly'),
                ariaLabel: t('designs:designGallery.tabs.displayOnlyAria'),
              },
            ]
          : []),
      ],
      sms: mobileTabConfig,
      ...(flagEnabled('ld-rcs') ? { rcs: mobileTabConfig } : {}),
      mps: [
        { id: tabs.MPS_EMBED, label: t('designs:designGallery.form.templateType.mpsEmbed') },
        { id: tabs.MPS_POPUP, label: t('designs:designGallery.form.templateType.mpsPopup') },
      ],
      default: [
        { id: tabs.CART_ABANDONMENTS, label: t('designs:designGallery.cartAbandonments') },
        { id: tabs.BROWSE_ABANDONMENTS, label: t('designs:designGallery.browseAbandonments') },
        { id: tabs.SEND_MY_BASKET, label: t('designs:designGallery.sendMyBasket') },
        { id: tabs.SUBJECT_LINES, label: t('designs:designGallery.subjectLines.subjectLines') },
        { id: tabs.UNSUBSCRIBES, label: t('designs:designGallery.unsubscribes') },
        {
          id: tabs.DATA_ACQUISITION,
          label: t('designs:designGallery.form.templateType.dataAcquisition'),
          ariaLabel: t('designs:designGallery.tabs.dataAcquisitionAria'),
        },
        {
          id: tabs.BULK_RETARGETING,
          label: t('designs:designGallery.bulkRetargeting'),
          ariaLabel: t('designs:designGallery.tabs.bulkRetargetingAria'),
        },
      ],
    };

    const tabsToRender = tabConfigs[channel as keyof typeof tabConfigs] || tabConfigs.default;

    return (
      <Tabs
        selectedTabId={selectedTabId}
        onSelectedTabChanged={tabId => {
          setSelectedTabId(tabId);
          updateURLQueryParam('templateType', tabId);
        }}
      >
        <TabBar>
          {tabsToRender.map(tab => (
            <Tab key={tab.id} tabId={tab.id} aria-label={tab.ariaLabel}>
              {tab.label}
            </Tab>
          ))}
          <TemplateSorting />
        </TabBar>
        <TabView overflow="visible">{getTabContent()}</TabView>
      </Tabs>
    );
  }, [channel, selectedTabId, getTabContent, t, flagEnabled]);

  if (isLoading) {
    return <Spinner />;
  }

  if (isError) {
    return (
      <Splash
        imageUrl="/images/error-plug-pink.svg"
        heading={t('designs:designGallery.templateList.queries.getSite.error')}
        imageHeight={150}
      />
    );
  }

  return (
    <LayoutWithStaticSidePanelRight>
      {renderTabs()}
      <TemplateListSidePanel
        isOpen={!!selectedTemplate}
        aria-label={
          selectedTemplate
            ? t('designs:designGallery.selectedTemplateSummaryAria', {
                templateName: selectedTemplate.templateName,
              })
            : undefined
        }
      >
        <SelectedTemplateSidebar />
      </TemplateListSidePanel>
    </LayoutWithStaticSidePanelRight>
  );
};

export default TemplatesList;
